import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { PlusIcon } from '@/components/catalog/icons';
import { TaskFormModal } from '@/components/tasks/TaskFormModal';
import { TaskList, TaskListSkeleton } from '@/components/tasks/TaskList';
import { Button, CollapsibleSection, EmptyState } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { ApiError, api } from '@/lib/api';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { denialReason } from '@/lib/permissions';
import { viewerZone } from '@/lib/task-time';
import { useAuth } from '@/lib/use-auth';
import type { Task, TaskListParams, TaskState, TaskTargetType } from '@/lib/types';

/*
 * The company page's "Tasks" section (owner, 2026-10-16: "Can't see previous
 * tasks in company profile, only ones that are outstanding — add all tasks of
 * previous, outstanding and upcoming on each company details page").
 *
 * EVERY task tied to the company — on the company row itself AND on its deals,
 * quotes, invoices and deliverables — in three tabs. The `ActivityDrawer`
 * behind the header's "Tasks & notes" button is unchanged and still shows the
 * tasks on the company row alone; this section is the wider read.
 *
 * ## The read
 *
 * `GET /tasks?relatedCompanyId=` — added to the API alongside this section.
 * The query is STRICT, so an older server answers 422 naming the unknown key;
 * on exactly that refusal this falls back ONCE (per fetch) to `companyId=`,
 * which is the company row only. Nothing is cached across fetches, so the
 * wider read starts working the moment the server ships it.
 *
 * `perPage` is capped at 100 server-side, so the section pages through up to
 * `MAX_PAGES` pages. Past that the list is truncated and the section SAYS SO
 * as a fact about the data ("Showing the first N of M tasks") — the tab counts
 * are then floors.
 *
 * ## The three buckets — the viewer's clock, compared on `due.utc`
 *
 *   | tab         | rule                                                         |
 *   | ----------- | ------------------------------------------------------------ |
 *   | Outstanding | open AND (undated OR due on/before TODAY in the viewer zone) |
 *   | Upcoming    | open AND due after today in the viewer zone                  |
 *   | Previous    | done or cancelled                                            |
 *
 * "Today" is the viewer's calendar date (`viewerZone(user.timezone)`), and a
 * due instant's day is ITS calendar date in that same zone — so "end of today"
 * is an honest midnight for the reader, never midnight UTC. `due.local` is
 * never parsed (see `task-time.ts`).
 *
 * ## Rows
 *
 * `TaskList` (compact) renders them — this file owns no row UI. A task on a
 * deal / quote / invoice / deliverable has its target label rewritten FOR
 * DISPLAY ONLY to "Deal · <name>" (or "Deal #id" when no name resolves); the
 * handlers always receive the ORIGINAL task, so nothing decorated reaches the
 * edit form or the wire. `TaskList` shows the target on every row, so a task
 * on the company itself shows the company name.
 *
 * ## Writes
 *
 * Gated on the reader's own grants, exactly as the drawer gates them. Every
 * accepted write calls `onChanged`, which the page turns into ONE reload token
 * shared with the drawer — so a write in either surface refreshes both this
 * section and the header button's counts.
 */

const PER_PAGE = 100;
const MAX_PAGES = 10;

type TaskBucket = 'outstanding' | 'upcoming' | 'previous';

const BUCKETS: readonly TaskBucket[] = ['outstanding', 'upcoming', 'previous'] as const;

function parseTaskBucket(raw: unknown): TaskBucket | undefined {
  return BUCKETS.find((bucket) => bucket === raw);
}

const BUCKET_LABEL: Record<TaskBucket, string> = {
  outstanding: 'Outstanding',
  upcoming: 'Upcoming',
  previous: 'Previous',
};

const EMPTY_TITLE: Record<TaskBucket, string> = {
  outstanding: 'No outstanding tasks',
  upcoming: 'No upcoming tasks',
  previous: 'No previous tasks',
};

const TARGET_NOUN: Record<TaskTargetType, string> = {
  deal: 'Deal',
  quote: 'Quote',
  invoice: 'Invoice',
  deliverable: 'Deliverable',
  company: 'Company',
};

/* --- the viewer's calendar day ------------------------------------------ */

const dayFormatters = new Map<string, Intl.DateTimeFormat | null>();

function dayFormatter(timeZone: string): Intl.DateTimeFormat | null {
  const hit = dayFormatters.get(timeZone);
  if (hit !== undefined) return hit;
  let made: Intl.DateTimeFormat | null;
  try {
    made = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone,
    });
  } catch {
    made = null;
  }
  dayFormatters.set(timeZone, made);
  return made;
}

/** `YYYY-MM-DD` of an instant in a zone, or `null` when it cannot be formed. */
function dayKey(instant: number, timeZone: string): string | null {
  const formatter = dayFormatter(timeZone);
  if (!formatter) return null;
  try {
    const parts = formatter.formatToParts(instant);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;
    return year && month && day ? `${year}-${month}-${day}` : null;
  } catch {
    return null;
  }
}

function dueMs(task: Task): number | null {
  if (!task.due || typeof task.due.utc !== 'string') return null;
  const ms = Date.parse(task.due.utc);
  return Number.isNaN(ms) ? null : ms;
}

function bucketOf(task: Task, nowMs: number, timeZone: string): TaskBucket {
  if (task.state !== 'open') return 'previous';
  const at = dueMs(task);
  // Undated — or a due time that does not parse — is work nobody has put off.
  if (at === null) return 'outstanding';
  const dueDay = dayKey(at, timeZone);
  const today = dayKey(nowMs, timeZone);
  if (dueDay === null || today === null) return at <= nowMs ? 'outstanding' : 'upcoming';
  return dueDay <= today ? 'outstanding' : 'upcoming';
}

function instantOf(value: string | null | undefined): number | null {
  if (typeof value !== 'string') return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/** Oldest due first, undated last (oldest created first among them). */
function compareOutstanding(a: Task, b: Task): number {
  const da = dueMs(a);
  const db = dueMs(b);
  if (da !== null && db !== null && da !== db) return da - db;
  if (da === null && db !== null) return 1;
  if (da !== null && db === null) return -1;
  return (instantOf(a.createdAt) ?? 0) - (instantOf(b.createdAt) ?? 0) || a.id - b.id;
}

/** Soonest first. Every row here is dated by construction. */
function compareUpcoming(a: Task, b: Task): number {
  return (dueMs(a) ?? 0) - (dueMs(b) ?? 0) || a.id - b.id;
}

/** Most recently closed first — `completedAt`, else the due time, else the last update. */
function closedAt(task: Task): number {
  return instantOf(task.completedAt) ?? dueMs(task) ?? instantOf(task.updatedAt) ?? 0;
}

function comparePrevious(a: Task, b: Task): number {
  return closedAt(b) - closedAt(a) || b.id - a.id;
}

const COMPARE: Record<TaskBucket, (a: Task, b: Task) => number> = {
  outstanding: compareOutstanding,
  upcoming: compareUpcoming,
  previous: comparePrevious,
};

/** The row as `TaskList` should LABEL it. Never handed back to a handler. */
function displayTask(task: Task): Task {
  const target = task.target;
  if (!target || target.type === 'company') return task;
  const name = target.name?.trim();
  const noun = TARGET_NOUN[target.type];
  return {
    ...task,
    target: { ...target, name: name ? `${noun} · ${name}` : `${noun} #${target.id}` },
  };
}

/* --- the read ------------------------------------------------------------ */

/** True for an older server's strict-query refusal of `relatedCompanyId`. */
function refusesRelatedFilter(cause: unknown): boolean {
  if (!(cause instanceof ApiError) || cause.status !== 422) return false;
  const details = cause.details ?? [];
  return (
    details.some(
      (detail) =>
        ('key' in detail && detail.key === 'relatedCompanyId') ||
        String(detail.path).includes('relatedCompanyId') ||
        String(detail.message).includes('relatedCompanyId'),
    ) || cause.message.includes('relatedCompanyId')
  );
}

interface TaskRead {
  tasks: Task[];
  /** The server's full count, so a truncated read can say so. */
  total: number;
}

async function readAllTasks(companyId: number, signal: AbortSignal): Promise<TaskRead> {
  let filter: TaskListParams = { relatedCompanyId: companyId };
  const base: TaskListParams = { perPage: PER_PAGE, sort: 'dueAt', order: 'asc' };

  let first;
  try {
    first = await api.listTasks({ ...base, ...filter, page: 1 }, signal);
  } catch (cause) {
    if (!refusesRelatedFilter(cause)) throw cause;
    // Older server: the company row only. Once, and never cached.
    filter = { companyId };
    first = await api.listTasks({ ...base, ...filter, page: 1 }, signal);
  }

  const tasks: Task[] = [...first.data];
  const lastPage = Math.min(first.meta.totalPages, MAX_PAGES);
  for (let page = 2; page <= lastPage; page += 1) {
    const next = await api.listTasks({ ...base, ...filter, page }, signal);
    tasks.push(...next.data);
    if (next.data.length === 0) break;
  }

  // A row can shift pages between requests; the same task must not render twice.
  const seen = new Set<number>();
  const unique = tasks.filter((task) => (seen.has(task.id) ? false : (seen.add(task.id), true)));
  return { tasks: unique, total: first.meta.total };
}

function loadMessage(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.status === 403) return 'You do not have permission to read these tasks.';
    if (cause.status === 404) return 'This company no longer exists.';
    return cause.message;
  }
  return 'Could not load the tasks. Check your connection and try again.';
}

/* --- icon ---------------------------------------------------------------- */

function TaskIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <path d="m3 6 2 2 3-3" />
      <path d="m3 14 2 2 3-3" />
      <path d="M12 7h9" />
      <path d="M12 15h9" />
    </svg>
  );
}

/* --- the section --------------------------------------------------------- */

interface TaskSlot {
  /** `${companyId}:${stamp}` — what this answer describes. */
  key: string;
  companyId: number;
  tasks: Task[] | null;
  total: number | null;
  error: string | null;
  /** The clock the buckets are cut against — taken when the answer landed. */
  readAt: number;
}

export interface CompanyTasksSectionProps {
  /** The shell's own id, from the page's `sectionShell`. */
  id: string;
  collapsed: boolean;
  onToggle: () => void;
  companyId: number;
  /** The page's shared reload token — bumped by this section and by the drawer. */
  reloadNonce: number;
  /** Fires on every write the server ACCEPTED. Never on a refusal. */
  onChanged: () => void;
}

export function CompanyTasksSection({
  id,
  collapsed,
  onToggle,
  companyId,
  reloadNonce,
  onChanged,
}: CompanyTasksSectionProps): ReactElement {
  const { ability, user } = useAuth();
  const canCreate = ability.canCreate('tasks');
  const canUpdate = ability.canUpdate('tasks');
  const canDelete = ability.canDelete('tasks');
  const updateDenial = canUpdate ? null : denialReason(ability, 'update', 'tasks');
  const viewerId = user?.id ?? null;
  const timeZone = viewerZone(user?.timezone);

  // The bucket is remembered on the account (owner, 2026-10-16).
  const [tab, setTab] = useRememberedState<TaskBucket>('company-tasks-bucket', 'outstanding', parseTaskBucket);
  const [retry, setRetry] = useState(0);
  const [slot, setSlot] = useState<TaskSlot | null>(null);
  const [form, setForm] = useState<{ task?: Task } | null>(null);
  const [busyTaskId, setBusyTaskId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const stampKey = `${companyId}:${reloadNonce}:${retry}`;

  useEffect(() => {
    const controller = new AbortController();
    const key = `${companyId}:${reloadNonce}:${retry}`;
    readAllTasks(companyId, controller.signal)
      .then((read) => {
        if (controller.signal.aborted) return;
        setSlot({
          key,
          companyId,
          tasks: read.tasks,
          total: read.total,
          error: null,
          readAt: Date.now(),
        });
        setActionError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setSlot({
          key,
          companyId,
          tasks: null,
          total: null,
          error: loadMessage(cause),
          readAt: Date.now(),
        });
      });
    return () => controller.abort();
  }, [companyId, reloadNonce, retry]);

  // Another company's answer never renders here; an older answer for THIS
  // company stays on screen, dimmed, while its refetch is in flight.
  const current = slot?.companyId === companyId ? slot : null;
  const loading = current === null;
  const refetching = current !== null && current.key !== stampKey;
  const error = current?.error ?? null;
  const tasks = current?.tasks ?? null;
  const readAt = current?.readAt ?? 0;

  const buckets = useMemo(() => {
    const out: Record<TaskBucket, Task[]> = { outstanding: [], upcoming: [], previous: [] };
    if (!tasks) return out;
    for (const task of tasks) out[bucketOf(task, readAt, timeZone)].push(task);
    for (const bucket of BUCKETS) out[bucket].sort(COMPARE[bucket]);
    return out;
  }, [tasks, readAt, timeZone]);

  const byId = useMemo(() => new Map((tasks ?? []).map((task) => [task.id, task])), [tasks]);
  const original = (task: Task): Task => byId.get(task.id) ?? task;

  const shown = tasks?.length ?? 0;
  const total = current?.total ?? null;
  const truncated = tasks !== null && total !== null && total > shown;

  function patch(change: (rows: Task[]) => Task[]) {
    setSlot((prev) =>
      prev && prev.companyId === companyId && prev.tasks !== null
        ? { ...prev, tasks: change(prev.tasks) }
        : prev,
    );
  }

  async function setTaskState(task: Task, next: TaskState) {
    setBusyTaskId(task.id);
    setActionError(null);
    try {
      const saved = await api.updateTask(task.id, { state: next });
      patch((rows) => rows.map((row) => (row.id === saved.data.id ? saved.data : row)));
      onChanged();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        patch((rows) => rows.filter((row) => row.id !== task.id));
      } else {
        setActionError(caught instanceof Error ? caught.message : 'Could not update the task.');
      }
    } finally {
      setBusyTaskId(null);
    }
  }

  async function removeTask(task: Task) {
    setBusyTaskId(task.id);
    setActionError(null);
    try {
      await api.deleteTask(task.id);
      patch((rows) => rows.filter((row) => row.id !== task.id));
      onChanged();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        patch((rows) => rows.filter((row) => row.id !== task.id));
      } else if (caught instanceof ApiError && caught.status === 403) {
        setActionError(
          'Only the person who asked for this task can delete it. Close it instead — that keeps the record that it was asked for.',
        );
      } else {
        setActionError(caught instanceof Error ? caught.message : 'Could not delete the task.');
      }
    } finally {
      setBusyTaskId(null);
    }
  }

  const summary =
    tasks === null
      ? undefined
      : BUCKETS.map((bucket) => `${buckets[bucket].length} ${BUCKET_LABEL[bucket].toLowerCase()}`).join(
          ' · ',
        );

  const addButton = canCreate ? (
    <Button size="sm" leadingIcon={<PlusIcon />} onClick={() => setForm({})}>
      Add task
    </Button>
  ) : undefined;

  const rows = buckets[tab];

  return (
    <CollapsibleSection
      id={id}
      title="Tasks"
      {...(summary ? { summary } : {})}
      actions={addButton}
      collapsed={collapsed}
      onToggle={onToggle}
    >
      <div className="flex flex-col gap-3 px-4 py-4">
        <ViewToggle<TaskBucket>
          label="Tasks section"
          value={tab}
          onChange={setTab}
          className="max-w-full"
          options={BUCKETS.map((bucket) => ({
            value: bucket,
            label: (
              <>
                {BUCKET_LABEL[bucket]}
                {tasks !== null && (
                  <span className="tabular text-fg-muted">{buckets[bucket].length}</span>
                )}
              </>
            ),
          }))}
        />

        {actionError && (
          <Notice tone="danger" role="alert">
            <span className="min-w-0 [overflow-wrap:anywhere]">{actionError}</span>
          </Notice>
        )}

        {loading ? (
          <div className="rounded-md border border-border">
            <TaskListSkeleton />
          </div>
        ) : error !== null ? (
          <Notice
            tone="danger"
            role="alert"
            title="Couldn’t load tasks."
            action={
              <Button size="sm" onClick={() => setRetry((value) => value + 1)}>
                Try again
              </Button>
            }
          >
            <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
          </Notice>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<TaskIcon />}
            title={EMPTY_TITLE[tab]}
            {...(tab !== 'previous' && addButton ? { action: addButton } : {})}
          />
        ) : (
          <div
            aria-busy={refetching || undefined}
            className={
              refetching
                ? 'rounded-md border border-border opacity-60 transition-opacity duration-150 ease-out motion-reduce:transition-none'
                : 'rounded-md border border-border'
            }
          >
            <TaskList
              variant="compact"
              tasks={rows.map(displayTask)}
              showTarget
              onToggleState={(task, next) => void setTaskState(original(task), next)}
              onEdit={(task) => setForm({ task: original(task) })}
              onDelete={(task) => void removeTask(original(task))}
              busyTaskId={busyTaskId}
              viewerTimeZone={timeZone}
              viewerId={viewerId}
              canUpdate={canUpdate}
              canDelete={canDelete}
              writeDenialReason={updateDenial}
            />
          </div>
        )}

        {/* A fact about the data, not advice: past the page cap the tabs are
            counting what was read, not everything there is. */}
        {truncated && total !== null && (
          <p className="m-0 text-xs text-fg-muted">
            Showing the first {shown.toLocaleString()} of {total.toLocaleString()} tasks.
          </p>
        )}
      </div>

      <TaskFormModal
        open={form !== null}
        onClose={() => setForm(null)}
        {...(form?.task ? { task: form.task } : {})}
        target={{ type: 'company', id: companyId }}
        // The modal closes itself — and stays open when an attachment upload
        // failed, to report it — so this only signals the reload.
        onSaved={() => onChanged()}
      />
    </CollapsibleSection>
  );
}
